/**
 * 화면 2 · 대기 중 (WaitingScreen) — 디자인 원본 Pantalla 2.
 *
 * @see #781 / admin#2111 설계 §6.1
 */
import { useState } from 'react';
import { Bell, Clock, MessageCircle, ShoppingBag, Users } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import type { Locale } from '@/config/locales';
import type { WaitlistPartyData, WaitlistStatusData } from '@/lib/api/waitlist';
import { WaitlistAppBar, WaitlistRow } from './WaitlistParts';
import { WaitlistMenuButton } from './WaitlistMenuButton';
import { resolveWaitlistContactDisplay } from './utils';
import type { WaitlistStrings } from './strings';

interface WaitlistWaitingScreenProps {
  t: WaitlistStrings;
  storeName: string;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  party: WaitlistPartyData;
  /** 등록 시점의 앞 팀 수 — Progress 분모. */
  startPosition: number;
  leaving: boolean;
  onLeave: () => void;
  /** 있으면 "메뉴 보기" 버튼을 노출한다(#795). */
  storeMeta?: WaitlistStatusData['store_meta'];
  /**
   * 서버가 아직 연락처 표시값을 주지 않을 때만 쓰는 폴백 — 등록 폼 입력값을
   * 마스킹한 문자열이다(admin#2504 / `maskWaitlistContact`).
   *
   * 메모리에만 산다. 새로고침하면 사라지고 서버 값만 남는 것이 의도다 — 번호(혹은
   * 그 파생값)를 새로 영속화하지 않는다는 것이 이 변경의 전제다.
   */
  contactFallback?: string | null;
}

export function WaitlistWaitingScreen({
  t,
  storeName,
  locale,
  onLocaleChange,
  party,
  startPosition,
  leaving,
  onLeave,
  storeMeta,
  contactFallback,
}: WaitlistWaitingScreenProps) {
  const pk = party.queue_type === 'pickup';
  // 표시 우선순위: 서버 마스킹 값 → 원문 → 등록 입력 폴백 → 생략(admin#2504).
  const contactDisplay = resolveWaitlistContactDisplay(party, contactFallback);
  const [dialogOpen, setDialogOpen] = useState(false);

  // startPosition 0 (등록 즉시 1번) 이면 분모가 0 이라 NaN 이 된다 — 진행률 100% 로 본다.
  const progress =
    startPosition <= 0
      ? 100
      : Math.min(100, Math.round(((startPosition - party.position) / startPosition) * 100));

  return (
    <>
      <WaitlistAppBar
        storeName={storeName}
        // 픽업은 "인원" 개념이 없다(서버가 party_size 를 1로 고정) — 테이블용 문구를
        // 그대로 재사용하면 좌석 정보가 잘못 노출된다(#794 Task 9 코드리뷰 시정).
        subtitle={pk ? t.pkSubWaiting(party.ticket_no) : t.subWaiting(party.ticket_no, party.party_size)}
        locale={locale}
        onLocaleChange={onLocaleChange}
        languageLabel={t.idioma}
        logoUrl={storeMeta?.logo_url}
        mapsUrl={storeMeta?.maps_url}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="px-5 pb-10 pt-1">
          <Card className="mb-4 overflow-hidden py-0">
            <div className="flex items-center justify-between bg-secondary px-5 py-[18px] text-secondary-foreground">
              <div>
                <div className="text-xs uppercase tracking-[0.06em] opacity-85">
                  {pk ? t.pkTuTurno : t.tuTurno}
                </div>
                <div className="text-[34px] font-extrabold leading-none">
                  <bdi dir="ltr">{party.ticket_no}</bdi>
                </div>
              </div>
              <Badge className="border-none bg-white/20 text-white">
                {pk ? t.pkWaitBadge : t.enEspera}
              </Badge>
            </div>
            <CardContent className="p-5 text-center">
              {party.position > 0 ? (
                <>
                  <div className="text-[60px] font-extrabold leading-none tracking-[-0.03em]">
                    {party.position}
                  </div>
                  <div className="mt-0.5 text-sm text-muted-foreground">
                    {/* party.position 은 "순번(ticket_no)"이 아니라 앞 대기 팀 수다 —
                        pkReservado('순번')를 쓰면 픽업에서도 오독을 유발한다(#794 리뷰).
                        테이블/픽업 공통으로 통용되는 "앞 팀 수" 문구(equipos)를 재사용한다. */}
                    {t.equipos(party.position)}
                  </div>
                </>
              ) : (
                <div className="text-[22px] font-extrabold text-primary">{t.casiTurno}</div>
              )}
              <div className="mb-2 mt-[18px]">
                <Progress value={progress} />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t.teUniste}</span>
                <span>{t.tuMesa}</span>
              </div>
              <div className="mt-[18px] inline-flex items-center gap-[7px] rounded-full bg-background-secondary px-3.5 py-2 text-[13px] font-semibold">
                <Clock className="size-4" />{' '}
                {pk ? t.pkEspera(party.est_minutes) : t.esperaAprox(party.est_minutes)}
              </div>
            </CardContent>
          </Card>

          <Card className="mb-4">
            <CardContent className="flex flex-col gap-3 px-4 py-3.5">
              <WaitlistRow
                icon={pk ? <ShoppingBag className="size-[17px]" /> : <Users className="size-[17px]" />}
                label={pk ? t.pkReservado : t.reservadoPara}
                value={pk ? party.ticket_no : t.nPersonas(party.party_size)}
              />
              {/* 표시할 연락처가 하나도 없으면 행 자체를 그리지 않는다 — 값이 빈
                  "알림" 한 줄은 안내가 아니라 고장으로 읽힌다. 구분선도 함께 뺀다. */}
              {contactDisplay !== null && (
                <>
                  <Separator />
                  <WaitlistRow
                    icon={<MessageCircle className="size-[17px]" />}
                    label={t.aviso}
                    value={<span dir="ltr">{contactDisplay}</span>}
                  />
                </>
              )}
              {/* SMS 백업 배지 — SMS provider 활성화(admin TWILIO_* env) 전까지 임시 숨김.
                  허위 안내 방지 (2026-07-20 사용자 결정). provider 활성화 시 복원할 것. */}
            </CardContent>
          </Card>

          <div className="flex gap-2.5 rounded-lg bg-background-secondary px-3.5 py-3 text-[13px] leading-relaxed text-muted-foreground">
            <span className="shrink-0">
              <Bell className="size-[17px]" />
            </span>
            <span>{t.tip}</span>
          </div>

          <div className="mt-5 flex flex-col gap-2.5">
            {storeMeta && (
              <WaitlistMenuButton
                t={t}
                menuViewUrl={storeMeta.menu_view_url}
                menuViewKind={storeMeta.menu_view_kind}
                menuUrl={storeMeta.menu_urls?.digital}
                menuPdfUrl={storeMeta.menu_urls?.pdf}
              />
            )}
            <Button
              variant="outline"
              className="w-full border-border font-bold text-destructive-dark"
              disabled={leaving}
              onClick={() => setDialogOpen(true)}
            >
              {t.salirFila}
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.dlgTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {pk ? t.pkDlgDesc(party.ticket_no) : t.dlgDesc(party.ticket_no, party.party_size)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDialogOpen(false)}>{t.seguir}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              disabled={leaving}
              onClick={() => {
                setDialogOpen(false);
                onLeave();
              }}
            >
              {t.siSalir}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
